import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { ClassAttendanceReportModel, ClassesAttendanceReportModel } from '@bayclubs/group-classes-employee-api';
import { gcManagementEnumerations } from '../../../interfaces/interfaces.module';
import { AttendanceClassItem, AttendanceDateSummary, AttendanceFilters } from '../../models/attendance-report.model';
import { ReportFilter, ReportType } from '../../models/reports-model';
import { ReportSelectService } from '../../services/report-select.service';
import { ReportsService } from '../../services/reports.service';
import { BaseReportComponent } from '../base/base-report.component';

@Component({
    selector: 'app-attendance-report',
    templateUrl: './attendance-report.component.html',
    styleUrls: ['./attendance-report.component.scss'],
    standalone: false
})
export class AttendanceReportComponent extends BaseReportComponent implements OnInit, OnDestroy {
    reportData: AttendanceDateSummary[];
    loadedReportData: AttendanceDateSummary[];
    backendData: ClassAttendanceReportModel[];


    constructor(
        activatedRoute: ActivatedRoute,
        reportSelectService: ReportSelectService,
        reportsService: ReportsService,
        translateService: TranslateService
    ) {
        super(activatedRoute, reportSelectService, reportsService, translateService);
    }

    ngOnInit() {
        this.reportType = ReportType.AttendanceReport;
        super.ngOnInit();
        this.reportData = [];
        this.loadedReportData = [];
        this.displayedClasses = [];
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    showReport(filter: ReportFilter) {
        if (filter === null) {
            this.generateDisabled = true;
            this.reportData = [];
            this.loadedReportData = [];
        } else {
            this.subscriptions.add(
                this.reportsService
                    .getAttendanceReportData(filter)
                    .subscribe((value: ClassesAttendanceReportModel) => {
                        if (value.groupClasses !== null) {
                            this.backendData = value.groupClasses;
                            this.generateReportData(value.groupClasses);
                            this.loadSelectedClasses(value.groupClasses, null);
                            this.loadSelectedInstructors(value.groupClasses);
                            if (this.reportData.length > 0) {
                                this.generateDisabled = false;
                            }
                        }
                    })
            );
        }
    }

    generateReportFile(filter: ReportFilter) {
        this.subscriptions.add(
            this.reportsService.generateAttendanceReportFile(filter).subscribe(value => {
                this.downloadReportFile(value, 'attendance_');
            })
        );
    }

    private generateReportData(groupClasses: ClassAttendanceReportModel[]) {
        let data: AttendanceDateSummary[] = [];

        groupClasses.forEach((groupClass: ClassAttendanceReportModel) => {
            let summaryItem = data.find(p => p.date.value === groupClass.classDate.value);
            if (summaryItem === undefined) {
                summaryItem = new AttendanceDateSummary(groupClass.classDate);
                data.push(summaryItem);
            }

            const item = new AttendanceClassItem();
            this.mapClassItem(groupClass, item);

            summaryItem.classes.push(item);
        });

        data = this.calculateAndSortData(data);
        this.reportData = data;
        this.loadedReportData = [...data];
    }

    private mapClassItem(groupClass: ClassAttendanceReportModel, classItem: AttendanceClassItem) {
        classItem.className = groupClass.className;
        classItem.billableLengthOfClass = groupClass.classBillableTimeInMinutes;
        classItem.actualLengthOfClass = groupClass.classTimeToInMinutes - groupClass.classTimeFromInMinutes;
        classItem.classAttendance = groupClass.classAttendance;
        classItem.cost = groupClass.cost;
        classItem.costPerHead = groupClass.costPerHead;
        classItem.clubName = groupClass.clubName;
        classItem.clubShortName = groupClass.clubShortName;
        classItem.category = groupClass.category;
        classItem.instructorName = groupClass.instructorName;
        classItem.instructorId = groupClass.instructorId;
        classItem.date = new Date(groupClass.classDate.value);
        classItem.bcDate = groupClass.classDate;
        classItem.startTime = groupClass.classTimeFromInMinutes;
        classItem.status = groupClass.classStatus;
        classItem.cancelled = classItem.status.code === gcManagementEnumerations.GroupClassStatus.cancelled.code;
        classItem.substitution = groupClass.isInstructorSubstituted;
    }

    private calculateAndSortData(data: AttendanceDateSummary[]): AttendanceDateSummary[] {
        return data.map(item => {
            item.totalHours = this.calculateSummaryRowTotalHours(item.classes);
            item.totalCost = this.calculateSummaryRowTotalCost(item.classes);
            item.classes = this.sort(item.classes, [
                { date: 'asc' },
                { startTime: 'asc' },
                { instructorName: 'asc' },
                { clubShortName: 'asc' }
            ]);
            return item;
        });
    }

    filterClass(filter: AttendanceFilters) {
        if (filter.instructors) {
            this.loadSelectedClasses(this.backendData, filter.instructors);
            if (filter.className && this.displayedClasses.filter(p => p.name === filter.className).length === 0) {
                filter.className = null;
            }
        }
        this.reportData = this.loadedReportData
            .map(data => {
                let accept = true;
                if (filter.className) {
                    if (data.classes.filter(p => p.className === filter.className).length > 0) {
                        accept = true;
                    } else {
                        accept = false;
                    }
                }
                if (filter.instructors) {
                    if (data.classes.filter(p => filter.instructors.includes(p.instructorId)).length > 0) {
                        accept = true;
                    } else {
                        accept = false;
                    }
                }
                if (accept) {
                    return this.generateSummaryRow(data, filter.className, filter.instructors);
                }
            })
            .filter(p => p !== undefined);
    }

    private generateSummaryRow(data: AttendanceDateSummary, className: string, instructors: string[]) {
        const retData = new AttendanceDateSummary(data.date);
        retData.classes = data.classes;
        if (instructors) {
            retData.classes = retData.classes.filter(p => instructors.includes(p.instructorId));
        }
        if (className) {
            retData.classes = retData.classes.filter(p => p.className === className);
        }
        retData.totalCost = this.calculateSummaryRowTotalCost(retData.classes);
        retData.totalHours = this.calculateSummaryRowTotalHours(retData.classes);
        return retData;
    }
}
